import { ref, computed, watch, unref } from 'vue';
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

export const applyLockedConversationOrder = (conversations, lockedIds) => {
  if (!lockedIds.length) return conversations;

  const conversationsById = new Map(
    conversations.map(conversation => [conversation.id, conversation])
  );
  const lockedIdSet = new Set(lockedIds);
  const conversationIndexById = new Map(
    conversations.map((conversation, index) => [conversation.id, index])
  );

  const kept = [];
  lockedIds.forEach(id => {
    const conversation = conversationsById.get(id);
    if (conversation) kept.push(conversation);
  });

  const incoming = conversations.filter(
    conversation => !lockedIdSet.has(conversation.id)
  );
  if (!incoming.length) return kept;

  const firstKeptIndex = conversationIndexById.get(kept[0]?.id);
  const prepended = [];
  const appended = [];

  incoming.forEach(conversation => {
    const index = conversationIndexById.get(conversation.id);
    if (firstKeptIndex === undefined || index < firstKeptIndex) {
      prepended.push(conversation);
    } else {
      appended.push(conversation);
    }
  });

  return [...prepended, ...kept, ...appended];
};

export const useLockedChatListOrder = (conversationListRaw, viewKey) => {
  const isChatListOrderLocked = ref(
    Boolean(LocalStorage.get(LOCAL_STORAGE_KEYS.CHAT_LIST_ORDER_LOCKED))
  );
  const lockedConversationIds = ref([]);
  const resolvedViewKey = computed(() => unref(viewKey) ?? '');

  const conversationList = computed(() => {
    const conversations = unref(conversationListRaw);
    if (!isChatListOrderLocked.value || !lockedConversationIds.value.length) {
      return conversations;
    }

    return applyLockedConversationOrder(
      conversations,
      lockedConversationIds.value
    );
  });

  const snapshotConversationIds = conversations => {
    lockedConversationIds.value = conversations.map(
      conversation => conversation.id
    );
  };

  watch(
    [() => unref(conversationListRaw), resolvedViewKey, isChatListOrderLocked],
    ([conversations, currentViewKey, locked], previous) => {
      if (!locked) {
        lockedConversationIds.value = [];
        return;
      }

      if (!conversations.length) {
        lockedConversationIds.value = [];
        return;
      }

      const previousViewKey = previous?.[1];
      const viewChanged =
        previousViewKey !== undefined && previousViewKey !== currentViewKey;

      if (viewChanged || !lockedConversationIds.value.length) {
        snapshotConversationIds(conversations);
        return;
      }

      lockedConversationIds.value = applyLockedConversationOrder(
        conversations,
        lockedConversationIds.value
      ).map(conversation => conversation.id);
    },
    { immediate: true }
  );

  watch(isChatListOrderLocked, locked => {
    LocalStorage.set(LOCAL_STORAGE_KEYS.CHAT_LIST_ORDER_LOCKED, locked);
  });

  return {
    isChatListOrderLocked,
    conversationList,
  };
};
